import React, { ReactNode } from 'react';
import { OnboardStep, OnboardStepId, useOnboardSteps } from '../../hooks/useOnboardSteps';
import { Button, ButtonSize } from '../catalyst-ui-kit/button';
import { ConnectButton } from '../ConnectButton/ConnectButton';

interface OnboardedButtonProps {
  /** The content to display when all onboarding steps are completed. */
  children: ReactNode;

  overrideLabel?: string;

  /** The highest onboard step required, defaults to the last. */
  dependsOnStep?: OnboardStepId;

  /** Called if the user clicks on an onboard step. */
  onOnboardCallback?: () => void;

  className?: string;

  size?: ButtonSize;
}

/**
 * OnboardedButton ensures that all onboard steps are completed before rendering
 * its children. If an onboard step is incomplete, it shows a button to
 * initiate the step.
 *
 * @example
 * <OnboardedButton>
 *   <Button onClick={placeOrder}>Place Order</Button>
 * </OnboardedButton>
 */
export const OnboardedButton: React.FC<OnboardedButtonProps> = ({
  children,
  overrideLabel,
  dependsOnStep,
  onOnboardCallback,
  className,
  size,
  ...rest
}) => {
  const { steps } = useOnboardSteps();

  const relevantSteps = dependsOnStep
    ? steps.slice(0, steps.findIndex((step) => step.id === dependsOnStep) + 1)
    : steps;

  const nextIncompleteStep: OnboardStep | undefined = relevantSteps.find((step) => !step.completed);

  return !nextIncompleteStep ? (
    <>{children}</>
  ) : nextIncompleteStep.id === 'connect' ? (
    <>
      <ConnectButton size={size} className={className} label={overrideLabel} />
    </>
  ) : (
    <Button
      size={size}
      color="blue"
      onClick={() => {
        nextIncompleteStep.activate?.(true);
        onOnboardCallback?.();
      }}
      className={className}
      {...rest}
    >
      {overrideLabel || nextIncompleteStep.buttonLabel || nextIncompleteStep.title}
    </Button>
  );
};
